import React, { useEffect, useMemo, useState } from "react";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import type { Engine, IOptions, RecursivePartial } from "@tsparticles/engine"; //Container
import { loadSlim } from "@tsparticles/slim"; // Import slim version for smaller bundle size
import { styled, useTheme } from "styled-components";
import { useLocation } from "react-router-dom";
import { useTheme as appTheme } from "../../context/ThemeContext";

const ParticleWrapper = styled.div`
  position: absolute;
  //   border: 1px solid red;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  will-change: transform, opacity;
  z-index: -1;
  pointer-events: none;
`;

const ParticleBackground: React.FC = () => {
  const [init, setInit] = useState(false);
  const defaultStyledTheme = useTheme();
  const { theme } = appTheme();
  const location = useLocation();
  const isContactPage = location.pathname === "/contact";

  // Initialize the particles engine only once
  useEffect(() => {
    initParticlesEngine(async (engine: Engine) => {
      await loadSlim(engine); // Load only necessary features for a smaller bundle
    }).then(() => {
      setInit(true);
    });
  }, []);

  // const particlesLoaded = (container: Container): Promise<void> => {
  //   if(container){
  //     console.log('particles container', container);
  //   }
  // };

  //Interaction
  //onClick push, repulse, remove, bubble, pause
  //onHover grab, repulse, bubble, connect, attract

  const particlesOptions = useMemo<RecursivePartial<IOptions>>(() => (
    {
      // to only occupy in the specified div
      fullScreen: {
        enable: false,
        zIndex: -1,
      },
      style: {
        position: "absolute",
        height: "100%",
        top: "0",
        left: "0",
      },
      background: {
        color: {
          value: "transparent", // Background color
        },
      },
      fpsLimit: 200,
      interactivity: {
        events: {
          onClick: {
            enable: true,
            mode: "repulse",
          },
          onHover: {
            enable: true,
            mode: isContactPage ? "bubble" : ["attract", "grab"],
          },
        },
        modes: {
          // push: {
          //     quantity: 4,
          // },
          repulse: {
            distance: 250,
            duration: 0.4,
          },
          bubble: {
            distance: 400, // Distance to start bubble effect
            size: 0, // Size of particles during bubble effect
            duration: 2, // Duration of the bubble effect
            opacity: 0, // Opacity of particles during bubble effect
          },
          grab: {
            distance: 100, // Distance to connect particles with lines
            line_linked: {
              opacity: 0.3, // Line opacity
            },
          },
          // connect: {
          //     distance: 120, // Connect particles within 120px of the cursor
          //     lineLinked: {
          //         opacity: 0.5, // Opacity of connection lines
          //     },
          //     radius: 100, // Radius around the cursor to connect particles
          // },
          attract: {
            distance: 200, // Attract particles within 200px of the cursor
            duration: 0.4, // Attraction effect lasts for 0.4 seconds
            easing: "ease-out-quad", // Smooth easing
            factor: 2, // Stronger attraction
            maxSpeed: 10, // Max speed of attraction
            speed: 2, // Speed at which particles move towards cursor
          },
        },
      },
      particles: {
        color: {
          value: defaultStyledTheme.text, // Particle color, using styled theme
        },
        links: {
          color: defaultStyledTheme.text,
          distance: 150,
          enable: false,
          opacity: 0.2,
          width: 1,
        },
        move: {
          direction: "none",
          enable: true,
          outModes: {
            default: "out", //bounce initially
          },
          random: true,
          speed: 3,
          straight: false,
        },
        number: {
          density: {
            enable: true,
          },
          value: isContactPage ? 60 : 120,
        },
        opacity: {
          value: { min: 0.2, max: theme === "dark" ? 6.0 : 0.3 },
        },
        shape: {
          type: "circle",
        },
        size: {
          value: { min: 0.5, max: 3 },
        },
      },
      detectRetina: true,
    }
  ), [defaultStyledTheme, isContactPage, theme]);

  return (
    <>
      {init && (
        <ParticleWrapper>
          <Particles
            id="tsparticles"
            // particlesLoaded={particlesLoaded}
            options={particlesOptions}
          />
        </ParticleWrapper>
      )}
    </>
  );
};

export default React.memo(ParticleBackground);
